import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Box, Divider, Grid, lighten, makeStyles, Theme, Typography, useTheme } from '@material-ui/core';
import { LoginLogoImg, Page } from '@roc/feature-app-core';
import { Paper, PaperBorderType, PreviewBackdrop } from '@roc/ui';
import clsx from 'clsx';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import { useEffect, useRef, useState } from 'react';
import { useDocumentLibraryRoutes } from './../../hooks/useDocumentLibraryRoutes';

const useStyles = makeStyles((theme: Theme) => ({
  documentContainer: {
    position: 'relative',
    height: '100%',
    padding: '16px',
    background: '#FFF',
    fontSize: 16,
    '& table': {
      width: '100%',
      textAlign: 'center',
      borderCollapse: 'collapse',
    },
    '& table th': {
      border: '0.25px solid black',
      background: lighten(theme.palette.primary.main, 0.8),
    },
    '& table td': {
      marginLeft: '5px',
      textAlign: 'left',
      borderBottom: '0.25px solid black',
      borderLeft: '0.25px solid black',
      borderRight: '0.25px solid black'
    },
    '& td p': {
      marginLeft: '5px'
    },
    '& ul,ol': {
      marginLeft: theme.spacing(3),
      marginBottom: theme.spacing(0.5),
    },
    '& h6': {
      fontSize: 16,
      padding: 4,
      fontWeight: 'bold',
    },
  },
  documentCard: {
    '& > div': {
      padding: 0
    },
    borderRadius: 0,
    marginBottom: theme.spacing(1),
    borderColor: '#aaa',
  },
  documentCardTable: {
    '& table th': {
      background: lighten(theme.palette.primary.main, 0.5),
      padding: "9px",
    }
  },
  documentCardTitle: {
    borderTop: '0.25px solid black',
    borderLeft: '0.25px solid black',
    borderRight: '0.25px solid black',
    justifyContent: 'center',
    textAlign: 'center',
    background: lighten(theme.palette.primary.main, 0.5),
    fontWeight: 'bold',
    padding: theme.spacing(0.5),
  }
}));


const usePrintModeStyles = makeStyles((theme: Theme) => ({
  documentContainer: {
    fontSize: 8,
    '& h6': {
      fontSize: 12,
    },
  },
}));

const DocumentCard = ({
  title = undefined,
  data,
  paperBorder = PaperBorderType.SOLID_BORDER,
  paperClassName = '',
  paperTitleClassName = '',
  paperBodyClassName = '',
  paperStyle = {},
}) => {
  const classes = useStyles();
  return (
    <Paper
      title={title}
      titleClass={clsx(classes.documentCardTitle, paperTitleClassName)}
      titleVariant={'h6'}
      borderType={paperBorder}
      className={clsx(classes.documentCard, paperClassName)}
      style={paperStyle}>
      <Box p={0.5} className={paperBodyClassName}>
        {documentToReactComponents(data)}
      </Box>
    </Paper>
  )
}

export const FNFAndGUCTearSheetHtml = ({
  contentId,
  data,
  preview = false,
  printMode = false,
  setContainerRef,
}: {
  contentId: string,
  data: any,
  preview?: boolean,
  printMode?: boolean,
  setContainerRef: any,
}) => {
  const theme = useTheme();
  const { documentLibraryRoutesConfig } = useDocumentLibraryRoutes();
  const classes = useStyles();
  const printModeClasses = usePrintModeStyles();
  const containerRef = useRef(null);

  const [printModeContainerStyle, setPrintModeContainerStyle] = useState<any>();
  const [containerStyle, setContainerStyle] = useState<any>();

  useEffect(() => {
    const doc = new jsPDF("p", "mm", "a4");
    let width: any = doc.internal.pageSize.getWidth();
    let height: any = doc.internal.pageSize.getHeight();
    width = `${Math.floor(width)}mm`;
    height = `${Math.floor(height)}mm`;
    setPrintModeContainerStyle({
      margin: 'auto',
      minWidth: width,
      maxWidth: width,
      height: height
    });
    setContainerStyle({
      margin: 'auto',
      maxWidth: theme.breakpoints.values.lg,
    });
  }, []);

  useEffect(() => {
    setContainerRef(containerRef);
  }, [containerRef])

  if (!data) {
    return null;
  }

  return (
    <div ref={containerRef} style={printMode ? printModeContainerStyle : containerStyle}>
      <Page routeDefinition={documentLibraryRoutesConfig.documentLibraryDocument(contentId)}>
        <Box className={clsx(classes.documentContainer, printMode ? printModeClasses.documentContainer : undefined)}>
          {preview && <PreviewBackdrop />}
          <LoginLogoImg imgStyle={{ maxWidth: 200 }} />
          <Box my={2} textAlign='center'>
            <Typography variant={'h5'}>
              <strong>{data.title}</strong>
            </Typography>
            <Divider style={{ marginTop: "1%", marginRight: "5.3%", marginLeft: "5.3%" }}></Divider>
            <p style={{ fontSize: "12px" }}>{data.headerText}</p>
          </Box>

          <Box mb={2} paddingRight="5%" paddingLeft="5%" paddingBottom="5%">
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <DocumentCard
                  data={data.flixAndFlipGroundCard}
                  paperClassName={classes.documentCardTable}
                  paperBorder={PaperBorderType.NO_BORDER}
                  paperStyle={{ marginBottom: "40px" }}
                />
                <DocumentCard
                  title={data.hardNoLoanTypeTitle}
                  data={data.hardNoLoanTypeCard}
                  paperBorder={PaperBorderType.NO_BORDER}
                  paperStyle={{ marginBottom: "30px" }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DocumentCard
                  title={data.loanClosingIssuesTitle}
                  data={data.loanClosingIssuesCard}
                  paperBorder={PaperBorderType.NO_BORDER}
                  paperStyle={{ marginBottom: "60px" }}
                />

                <DocumentCard
                  title={data.letterOfExplanationRequirementsCreditTitle}
                  data={data.letterOfExplanationRequirementsCreditCard}
                  paperBorder={PaperBorderType.NO_BORDER}
                  paperStyle={{ paddingBottom: "2%" }}
                />

                <DocumentCard
                  title={data.letterOfExplanationBackgroundTitle}
                  data={data.letterOfExplanationBackgroundCard}
                  paperBorder={PaperBorderType.NO_BORDER}
                  paperStyle={{ paddingBottom: "2%" }}
                />

              </Grid>
            </Grid>
          </Box>
          <Box m={1} position={'absolute'} right={0} bottom={0} textAlign='right' color={'#ccc'}>
            <Typography variant={'body2'}>
              {`Last Updated: ${format(new Date(data.lastUpdatedDate), 'MM-dd-yyyy')}`}
            </Typography>
          </Box>
        </Box>
      </Page>

      <Page routeDefinition={documentLibraryRoutesConfig.documentLibraryDocument(contentId)}>
        <Box className={clsx(classes.documentContainer, printMode ? printModeClasses.documentContainer : undefined)}>
          <LoginLogoImg imgStyle={{ maxWidth: 200 }} />
          <Box my={2} textAlign='center'>
            <Typography variant={'h5'}>
              <strong>{data.title}</strong>
            </Typography>
          </Box>


          <Box mb={2} paddingRight="5%" paddingLeft="5%" paddingBottom="5%">
            <DocumentCard
              title={data.problemLoanTypesAndGuidelinesTitle}
              data={data.problemLoanTypesAndGuidelinesCard}
              paperBorder={PaperBorderType.NO_BORDER}
            />
          </Box>
          <Box m={1} position={'absolute'} right={0} bottom={0} textAlign='right' color={'#ccc'}>
            <Typography variant={'body2'}>
              {`Last Updated: ${format(new Date(data.lastUpdatedDate), 'MM-dd-yyyy')}`}
            </Typography>
          </Box>
        </Box>
      </Page>
    </div>
  );
}